import {
  ContainerClassKey,
  ButtonBaseClassKey,
  LinkClassKey,
  TableSortLabelClassKey,
  TableRowClassKey,
  TableCellClassKey,
} from '@material-ui/core';
import { ButtonClassKey } from '@material-ui/core/Button';
import createBreakpoints from '@material-ui/core/styles/createBreakpoints';
import { Overrides } from '@material-ui/core/styles/overrides';
import { CSSProperties } from '@material-ui/core/styles/withStyles';
import { maxWidthMap, TextColors, SecondaryColors, colorMap } from '@/utils/constants';
import backgroundColors from './background-colors';
import typography from './typography';

const breakpoints = createBreakpoints({});

const MuiContainer: Partial<Record<ContainerClassKey, CSSProperties>> = {
  maxWidthLg: {
    paddingLeft: '32px',
    paddingRight: '32px',
    maxWidth: maxWidthMap.large,
    position: 'relative',

    '&.medium': {
      maxWidth: maxWidthMap.medium,
    },

    '&.small': {
      maxWidth: maxWidthMap.small,
    },

    [breakpoints.up('md')]: {
      maxWidth: maxWidthMap.large,

      '&.fullWidth': {
        maxWidth: '100%',
      },

      '&.large': {},

      '&.largeRight': {
        maxWidth: `calc(((100% - ${maxWidthMap.large}px) / 2) + ${maxWidthMap.large}px)`,
        margin: '0 0 0 auto',
        paddingRight: 0,
      },

      '&.medium': {
        maxWidth: maxWidthMap.medium,
      },

      '&.small': {
        maxWidth: maxWidthMap.small,
      },
    },
  },
};

const MuiButton: Partial<Record<ButtonClassKey, CSSProperties>> = {
  root: {
    ...typography.button,
    fontWeight: 500,
  },
  containedSecondary: {
    color: TextColors.dark,
    '&:hover': {
      backgroundColor: SecondaryColors.light,
    },
  },
  outlinedSecondary: {
    overflow: 'hidden',

    '&:before': {
      backgroundColor: '#000000',
      content: '""',
      height: '100%',
      left: 0,
      opacity: 0,
      position: 'absolute',
      top: 0,
      transition: 'opacity 150ms ease-in-out',
      width: '100%',
    },

    '&:hover:before': {
      opacity: 0.05,
    },
  },
  contained: {
    paddingBottom: '10px',
    paddingTop: '10px',
  },
  outlined: {
    paddingBottom: '9px',
    paddingTop: '9px',
  },
  label: {
    position: 'relative',

    '& img': {
      width: '20px',
      height: '20px',
    },
  },
};

const MuiLink: Partial<Record<LinkClassKey, CSSProperties>> = {
  root: {
    ...typography.button,
  },
};

const MuiButtonBase: Partial<Record<ButtonBaseClassKey, CSSProperties>> = {
  root: {
    outline: undefined,
  },
};

const MuiCssBaseline: {
  '@global'?: {
    '@font-face'?: CSSProperties['@font-face'];
  } & Record<string, CSSProperties['@font-face'] | CSSProperties>; // allow arbitrary selectors
} = {
  '@global': {
    '@keyframes enter': {
      '0%': {
        opacity: 0,
      },
      '100%': {
        opacity: 1,
      },
    },
    root: {
      outline: undefined,
    },
    html: {
      WebkitFontSmoothing: 'antialiased',
      'overflow-x': 'hidden',

      '&.animate-enter': {
        animation: 'enter 250ms ease-out',
      },

      '& *': {
        fontVariantLigatures: 'none !important',
      },

      '&:not(.a11y) *:focus': {
        outline: 'none !important',
      },

      '& .click-here-placeholder, & .configured-placeholder': {
        display: 'none',
      },

      '&.hippo-show-components': {
        '& .not-configured-label': {
          display: 'none',
        },

        '& .click-here-placeholder': {
          display: 'inline',
        },

        '& .configured-placeholder': {
          display: 'block',
        },
      },
    },
    body: {
      minHeight: '100dvh',
      display: 'flex',
      flexDirection: 'column',
      color: TextColors.dark,
      backgroundColor: colorMap['dark-gray'],
      willChange: 'scroll-position',
    },
    '*': {
      boxSizing: 'border-box',
    },
    img: {
      maxWidth: '100%',
    },
    iframe: {
      width: '100%',
    },
    'h2 sup, h4 sup': {
      ...typography?.secondarySup,
    },
    '* sup': {
      ...typography?.primarySup,
    },
    'div[data-datalayer-position]': {
      width: '100%',
    },
    '#root': {
      // flexGrow: 1,
      // display: 'flex',
      // flexDirection: 'column',
      // justifyContent: 'space-evenly',
      // '& .main': {
      //   display: 'flex',
      //   flexDirection: 'column',
      //   flexGrow: 1,
      //   '& > *:not(.hippo-overlay-box)': {
      //     '&:first-child:not(:empty)': {
      //       display: 'flex',
      //       flexDirection: 'column',
      //       flexGrow: 1,
      //       '& > *:last-child:not(:empty)': {
      //         display: 'flex',
      //         flexDirection: 'column',
      //         flexGrow: 1,
      //         '& > .gtm-container': {
      //           '&, & .gtm-container': {
      //             display: 'flex',
      //             flexDirection: 'column',
      //             flexGrow: 1,
      //             '& > *:last-child': {
      //               flexGrow: 1,
      //             },
      //           },
      //         },
      //       },
      //     },
      //   },
      // },
      // '& .hippo-overlay-box:not(.hippo-overlay-box-container-filled)': {
      //   flexGrow: 1,
      //   // '& > *': {
      //   //   width: '100%',
      //   // },
      //   // Fixes CMS dropping issue
      //   '&.qa-dragula-component.gu-transit': {
      //     pointerEvents: 'none',
      //   },
      // },
    },
    '.grecaptcha-badge': { visibility: 'hidden' },
    'form.mktoForm': { padding: '0 !important' },
    ...backgroundColors(breakpoints),
  },
};

const MuiTableRow: Partial<Record<TableRowClassKey, CSSProperties>> = {
  root: {
    color: TextColors.dark,
    transition: 'background-color 150ms ease-in-out',

    '&:hover': {
      color: TextColors.dark,
    },
  },
  hover: {
    '&:hover': {
      backgroundColor: `${SecondaryColors.light} !important`,
    },
  },
};

const MuiTableCell: Partial<Record<TableCellClassKey, CSSProperties>> = {
  root: {
    color: TextColors.dark,
    padding: '5px 10px',
    minWidth: '100px',
  },
};

const MuiTableSortLabel: Partial<Record<TableSortLabelClassKey, CSSProperties>> = {
  root: {
    '&:focus': {
      color: TextColors.light,
    },
    '&:hover': {
      color: TextColors.light,

      '& $icon': {
        opacity: 1,
      },
    },

    '&& $icon': {
      opacity: 0.5,
      color: TextColors.light,
    },

    '&$active': {
      color: TextColors.light,

      '&& $icon': {
        opacity: 1,
        color: TextColors.light,
      },
    },
  },
};

const overrides: Overrides = {
  MuiContainer,
  MuiButton,
  MuiLink,
  MuiButtonBase,
  MuiCssBaseline,
  MuiTableRow,
  MuiTableCell,
  MuiTableSortLabel,
};

export default overrides;
